import { unstable_composeClasses as composeClasses } from '@ori-ui/mui-bundle/base';
import { styled, useThemeProps, useForkRef, Skeleton } from '@ori-ui/mui-bundle/material';
import clsx from 'clsx';
import { forwardRef, useEffect, useRef, useState } from 'react';
import SuspenseImage from '../SuspenseImage';
import useIntersectionObserver from '../useIntersectionObserver';
import { getLazyImageUtilityClass } from './lazyImageClasses';
import { jsx as _jsx } from "react/jsx-runtime";
export const getLazyImageSlots = _ => ({
  root: ['root'],
  skeleton: ['skeleton'],
  image: ['image']
});
const useUtilityClasses = ownerState => {
  const {
    classes
  } = ownerState;
  const slots = getLazyImageSlots(ownerState);
  const composedClasses = composeClasses(slots, getLazyImageUtilityClass, classes);
  return {
    ...classes,
    ...composedClasses
  };
};
const LazyImageRoot = styled('span', {
  name: 'MuiOriLazyImage',
  slot: 'Root',
  overridesResolver: (_, styles) => [styles.root]
})(({
  ownerState: {
    width,
    height
  }
}) => ({
  display: 'inline-block',
  height,
  width
}));
const LazyImageSkeleton = styled(Skeleton, {
  name: 'MuiOriLazyImage',
  slot: 'Skeleton',
  overridesResolver: (_, styles) => [styles.skeleton]
})({
  display: 'inline-block',
  height: '100%',
  width: '100%'
});
const LazyImageImg = styled('img', {
  name: 'MuiOriLazyImage',
  slot: 'Image',
  overridesResolver: (_, styles) => [styles.image]
})({
  display: 'inline-block',
  height: '100%',
  width: '100%'
});
const InnerSuspendedImage = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    loading,
    didIntersect,
    classes,
    src,
    srcSet,
    imgProps,
    SuspenseImageProps,
    errorFallback,
    alt
  } = props;
  const imageProps = {
    errorFallback,
    alt
  };
  if (loading === 'eager' || didIntersect) {
    return /*#__PURE__*/_jsx(LazyImageImg, {
      as: SuspenseImage,
      ...imageProps,
      ref: ref,
      className: classes?.image,
      src: src,
      srcSet: srcSet,
      ...imgProps,
      ...SuspenseImageProps
    });
  }

  // Do not mount suspended image
  return null;
});
const InnerLazyImage = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    loading,
    didIntersect,
    errorFallback,
    classes,
    fallback = /*#__PURE__*/_jsx(LazyImageSkeleton, {
      animation: "wave",
      className: classes?.skeleton,
      variant: "rectangular"
    }),
    src,
    srcSet,
    imgProps,
    alt
  } = props;
  const imageProps = {
    alt
  };
  const [isLoaded, setIsLoaded] = useState(loading === 'eager');
  const [error, setError] = useState();
  useEffect(() => {
    if (loading === 'eager' || loading === 'lazy' && !didIntersect) {
      return () => {};
    }
    const onLoad = () => setIsLoaded(true);
    const onError = event => setError(event);
    const image = new Image();
    image.addEventListener('load', onLoad);
    image.addEventListener('error', onError);
    if (srcSet) {
      image.srcset = srcSet;
    }
    image.src = src;
    image.alt = alt ?? '';
    return () => {
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onError);
    };
  }, [loading, didIntersect, src, srcSet, alt]);
  if (isLoaded) {
    return /*#__PURE__*/_jsx(LazyImageImg, {
      ...imageProps,
      ref: ref,
      src: src,
      srcSet: srcSet,
      className: classes?.image,
      ...imgProps
    });
  }
  if (error !== undefined) {
    return errorFallback;
  }
  return fallback;
});
export const LazyImage = /*#__PURE__*/forwardRef((inProps, inRef) => {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiOriLazyImage'
  });
  const wrapperRef = useRef();
  const ref = useForkRef(inRef, wrapperRef);
  const {
    className,
    sx,
    component,
    loading = 'lazy',
    shouldThrowError = false,
    enableSuspense = false,
    errorFallback = null,
    fallback,
    imgRef,
    width,
    height,
    src,
    srcSet,
    SuspenseImageProps,
    imgProps,
    ...otherProps
  } = props;
  const {
    didIntersect
  } = useIntersectionObserver({
    ref: wrapperRef,
    disabled: loading === 'eager',
    unmountAfterIntersection: true
  });
  const ownerState = {
    ...props,
    SuspenseImageProps,
    imgProps,
    src,
    srcSet,
    width,
    height,
    loading,
    shouldThrowError,
    errorFallback,
    didIntersect
  };
  const classes = useUtilityClasses(ownerState);
  const InnerComponent = enableSuspense ? InnerSuspendedImage : InnerLazyImage;
  return /*#__PURE__*/_jsx(LazyImageRoot, {
    ref: ref,
    ownerState: ownerState,
    sx: sx,
    as: component,
    className: clsx(classes.root, className),
    ...otherProps,
    children: /*#__PURE__*/_jsx(InnerComponent, {
      ...ownerState,
      ref: imgRef,
      classes: classes
    })
  });
});
export default LazyImage;
//# sourceMappingURL=LazyImage.js.map